import { Gif } from '@giphy/react-components';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import CardMedia from '@material-ui/core/CardMedia';
import CircularProgress from '@material-ui/core/CircularProgress';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import Done from '@material-ui/icons/Done';
import ExpandMore from '@material-ui/icons/ExpandMore';
import HelpOutline from '@material-ui/icons/HelpOutline';
import SkipNext from '@material-ui/icons/SkipNext';
import Visibility from '@material-ui/icons/Visibility';
import { filter, isEmpty, map, size, split } from 'lodash';
import { TextField } from 'mui-rff';
import React, { FC } from 'react';
import { Form } from 'react-final-form';
import { RoundStatus, useGiphy, useHints, usePlayers, useRound } from './hooks';
import Screen from './Screen';

const getCircularReplacer = () => {
  const seen = new WeakSet();
  return (key: any, value: any) => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) {
        return;
      }
      seen.add(value);
    }
    return value;
  };
};

const Objektkarte: FC<{ objekt: object}> = ({ objekt }) => (
  <pre>{JSON.stringify(objekt, getCircularReplacer(), 2)}</pre>
);

const GuesserCollectingHints = () => {
  const { round, moderator } = useRound();
  const gif = useGiphy('sleeping cat');
  if (!round || !moderator) {
    return <CircularProgress />;
  }
  return (
    <Screen title="GuesserCollectingHints">
      <Card>
        <CardHeader
          title="Warte auf die Hinweisgeber"
          subheader={`Warte, bis alle Hinweisgeber ihre Hinweise eingeloggt haben und der Moderator ${moderator.name} ungültige Hinweise entfernt und alle verbleibenden Hinweise aufgedeckt hat.`}
        />
        {gif &&
          <CardMedia
            component={Gif}
            gif={gif}
            width={600}
            backgroundColor="white"
            hideAttribution
          />
        }
      </Card>
    </Screen>
  );
};

const TipsterCollectingHints = () => {
  const { round, guesser, addHint, hintSubmitted } = useRound();
  if (!round || !guesser) {
    return <CircularProgress />;
  }
  if (hintSubmitted) {
    return <TipsterCollectedHints/>
  }
  return (
    <Screen title="TipsterCollectingHints">
      <Form
        onSubmit={({ term }) => addHint(term)}
        validate={({ term }) => {
          const errors = {} as { term?: string };
          if (!term) {
            errors.term = 'Es muss ein Hinweis auf das geheime Wort eingegeben werden.';
          }
          if (size(split(term, ' ')) > 1) {
            errors.term = 'Der Hinweis darf nicht länger als ein Wort sein.';
          }
          return errors;
        }}
          render={({ handleSubmit, invalid, pristine, submitting }) => (
          <Card>
            <CardHeader
              title="Notiere einen Hinweis auf das geheime Wort"
              subheader={`Das geheime Wort muss von ${guesser.name} erraten werden.`}
            />
            <CardContent>
              <ExplanationInvalidHints/>
            </CardContent>
            <CardContent>
              <Typography variant="h6">Geheimes Wort:</Typography>
              <Typography variant="h3" align="center">{round.term}</Typography>
            </CardContent>
            <CardContent>
              <TextField name="term" label="Mein Hinweis" variant="filled" required/>
            </CardContent>
            <CardActions>
              <Button
                disabled={invalid || pristine || submitting}
                variant="contained"
                startIcon={<Done/>}
                onClick={handleSubmit}
              >
                Hinweis einloggen
              </Button>
            </CardActions>
          </Card>
        )}
      />
    </Screen>
  );
};

const ExplanationInvalidHints = () => (
  <ExpansionPanel>
    <ExpansionPanelSummary expandIcon={<ExpandMore/>}>
      <Typography>Spielregel "Ungültige Hinweise":</Typography>
    </ExpansionPanelSummary>
    <ExpansionPanelDetails>
      <Typography>
        <ul>
          <li>
            Das geheime Wort in einer <strong>anderen Schreibweise</strong>.
            <br/>
            <i>Beispiel: "Cäsar" ist als Hinweis für Caesar ungültig.</i>
          </li>
          <li>
            Das geheime Wort in einer <strong>anderen Sprache</strong>.
            <br/>
            <i>Beispiel: "Black" ist als Hinweis für Schwarz ungültig.</i>
          </li>
          <li>
            Ein Wort mit <strong>demselben Wortstamm</strong> wie das geheime Wort.
            <br/>
            <i>Beispiel: "Prinzessin" ist als Hinweis für Prinz ungültig.</i>
          </li>
          <li>
            Ein <strong>erfundenes Wort</strong>.
            <br/>
            <i>Beispiel: "Süßding" ist als Hinweis für Torte ungültig.</i>
          </li>
          <li>
            Ein Wort, das <strong>ähnlich klingt</strong> wie das geheime Wort, aber <strong>eine andere Bedeutung</strong> hat.
            <br/>
            <i>Beispiel: "Schweiß" ist als Hinweis für Schweiz ungültig.</i>
          </li>
        </ul>
      </Typography>
    </ExpansionPanelDetails>
  </ExpansionPanel>
);

const ExplanationIdenticalHints = () => (
  <ExpansionPanel>
    <ExpansionPanelSummary expandIcon={<ExpandMore/>}>
      <Typography>Spielregel "Identische Hinweise":</Typography>
    </ExpansionPanelSummary>
    <ExpansionPanelDetails>
      <Typography>
        <ul>
          <li>
            Zwei <strong>identische Wörter</strong>.
            <br/>
            <i>Beispiel: "Maus" und "Maus".</i>
          </li>
          <li>
            Verschiedene <strong>Varianten mit demselben Wortstamm</strong>.
            <br/>
            <i>Beispiel: "Spieler" und "Spielfeld"</i>
          </li>
          <li>
            Verschiedene <strong>Varianten desselben Wortes</strong>:
            Die Mehrzahl, unterschiedliche Geschlechter, Rechtschreibvarianten usw.
            <br/>
            <i>Beispiele: "Prinz" und "Prinzen", "Bäcker" und "Bäckerin", "Fotografie" und "Photographie"</i>
          </li>
        </ul>
      </Typography>
    </ExpansionPanelDetails>
  </ExpansionPanel>
);

export const TipsterCollectedHints = () => {
  const { round, guesser, moderator, isModerator, discoverGuesses } = useRound();
  const { hints } = useHints();
  const { activePlayers } = usePlayers();
  if (!round || !guesser || !moderator || !hints) {
    return <CircularProgress />;
  }
  const hintsMissing = size(hints) + 1 < size(activePlayers);
  return (
    <Screen title="TipsterCollectedHints">
      <Card>
        <CardHeader
          title="Folgende Hinweise wurden gegeben"
          subheader={
            isModerator ?
            `Du bist Moderator. Wähle eindeutige und gültige Hinweise aus, damit ${guesser.name} das geheime Wort erraten kann.` :
            `Der Moderator ${moderator.name} wählt eindeutige und gültige Hinweise aus.`
          }
        />
        <CardContent>
          <ExplanationIdenticalHints/>
          <ExplanationInvalidHints/>
        </CardContent>
        <CardContent>
          <Typography variant="h6">Hinweise für das geheime Wort '{round.term}':</Typography>
          <Hints switches={isModerator} showInvalid/>
          {hintsMissing &&
          <Typography>Es wurden noch nicht von allen Hinweisgebern Hinweise eingeloggt.</Typography>
          }
        </CardContent>
        <CardActions>
          {isModerator &&
          <Button
            variant="contained"
            disabled={hintsMissing}
            startIcon={<Visibility/>}
            onClick={discoverGuesses}
          >
            Hinweise aufdecken
          </Button>
          }
        </CardActions>
      </Card>
    </Screen>
  );
};

const GuesserGuessing = () => {
  const { round, onGuess, onNoGuess } = useRound();
  if (!round) {
    return <CircularProgress />;
  }
  return (
    <Screen title="GuesserGuessing">
      <Form
        onSubmit={async ({ guess }) => onGuess(guess)}
        validate={({ guess }) => {
          const errors = {} as { guess?: string };
          if (!guess) {
            errors.guess = 'Es muss ein Begriff eingegeben werden';
          }
          return errors;
        }}
        render={({ handleSubmit, invalid, pristine, submitting }) => (
          <Card>
            <CardHeader
              title="Rateversuch"
              subheader="Welches geheime Wort wird durch die Hinweise beschrieben?"
            />
            <CardContent>
              <Hints switches={false}/>
            </CardContent>
            <CardContent>
              <TextField name="guess" label="Geheimes Wort" variant="filled" required/>
            </CardContent>
            <CardActions>
              <Button
                disabled={invalid || pristine || submitting}
                variant="contained"
                startIcon={<Done/>}
                onClick={handleSubmit}
              >
                Rateversuch einloggen
              </Button>
              <Button
                disabled={submitting}
                variant="contained"
                startIcon={<HelpOutline/>}
                onClick={onNoGuess}
              >
                Keine Ahnung
              </Button>
           </CardActions>
          </Card>
          )}
      />
    </Screen>
  );
};

const TipsterGuessing = () => {
  const { round, guesser } = useRound();
  const gif = useGiphy('waiting cat');
  if (!round || !guesser) {
    return <CircularProgress />;
  }
  return (
    <Screen title="TipsterGuessing">
      <Card>
        <CardHeader
          title={`Warten auf Rateversuch`}
          subheader={`${guesser.name} muss das geheime Wort "${round.term}" erraten`}
        />
        <CardContent>
          <Typography variant="h6">
            Hinweise der Hinweisgeber:
          </Typography>
          <Hints switches={false}/>
        </CardContent>
        {gif &&
          <CardMedia
            component={Gif}
            gif={gif}
            width={600}
            backgroundColor="white"
            hideAttribution
          />
        }
      </Card>
    </Screen>
  );
};

const GuesserCorrect = () => {
  const { round, nextRound } = useRound();
  const gif = useGiphy('happy cat');
  if (!round) {
    return <CircularProgress />;
  }
  return (
    <Screen title="GuesserCorrect">
      <Card>
        <CardHeader
          title="Erfolg"
          subheader={`Du hast das geheime Wort '${round.term}' erraten`}
        />
        <CardContent>
          <Typography variant="h6">Du hattest folgende Hinweise:</Typography>
          <Hints switches={false}/>
        </CardContent>
        {gif &&
          <CardMedia
            component={Gif}
            gif={gif}
            width={600}
            backgroundColor="white"
            hideAttribution
          />
        }
        <CardActions>
          <Button
            variant="contained"
            startIcon={<SkipNext/>}
            onClick={nextRound}
          >
            weiter
          </Button>
        </CardActions>
      </Card>
    </Screen>
  );
};

const TipsterCorrect = () => {
  const { round, guesser, nextRound } = useRound();
  const gif = useGiphy('yes cat');
  if (!round || !guesser) {
    return <CircularProgress />;
  }
  return (
    <Screen title="TipsterCorrect">
      <Card>
        <CardHeader
          title="Erfolg"
          subheader={`${guesser.name} hat das geheime Wort '${round.term}' erraten`}
        />
        <CardContent>
          <Typography variant="h6">{guesser.name} hatte folgende Hinweise:</Typography>
          <Hints switches={false}/>
        </CardContent>
        {gif &&
          <CardMedia
            component={Gif}
            gif={gif}
            width={600}
            backgroundColor="white"
            hideAttribution
          />
        }
        <CardActions>
          <Button
            variant="contained"
            startIcon={<SkipNext/>}
            onClick={nextRound}
          >
            weiter
          </Button>
        </CardActions>
      </Card>
    </Screen>
  );
};

const GuesserWrong = () => {
  const { round, nextRound } = useRound();
  const gif = useGiphy('sad cat');
  if (!round) {
    return <CircularProgress />;
  }
  return (
    <Screen title="GuesserWrong">
      <Card>
        <CardHeader
          title="Misserfolg"
          subheader={`Du hattest '${round.guess}' eingeloggt. Damit hast du das geheime Wort '${round.term}' leider nicht erraten.`}
        />
        <CardContent>
          <Typography variant="h6">Du hattest folgende Hinweise:</Typography>
          <Hints switches={false}/>
        </CardContent>
        {gif &&
          <CardMedia
            component={Gif}
            gif={gif}
            width={600}
            backgroundColor="white"
            hideAttribution
          />
        }
        <CardActions>
          <Button
            variant="contained"
            startIcon={<SkipNext/>}
            onClick={nextRound}
          >
            weiter
          </Button>
        </CardActions>
      </Card>
    </Screen>
  );
};

const TipsterWrong = () => {
  const { round, guesser, nextRound } = useRound();
  const gif = useGiphy('no cat');
  if (!round || !guesser) {
    return <CircularProgress />;
  }
  return (
    <Screen title="TipsterWrong">
      <Card>
        <CardHeader
          title="Misserfolg"
          subheader={`${guesser.name} hatte '${round.guess}' eingeloggt. Damit hat ${guesser.name} das geheime Wort '${round.term}' leider nicht erraten.`}
        />
        <CardContent>
          <Typography variant="h6">{guesser.name} hatte folgende Hinweise:</Typography>
          <Hints switches={false}/>
        </CardContent>
        {gif &&
          <CardMedia
            component={Gif}
            gif={gif}
            width={600}
            backgroundColor="white"
            hideAttribution
          />
        }
        <CardActions>
          <Button
            variant="contained"
            startIcon={<SkipNext/>}
            onClick={nextRound}
          >
            weiter
          </Button>
        </CardActions>
      </Card>
    </Screen>
  );
};

const GuesserNoGuess = () => {
  const { round, nextRound } = useRound();
  const gif = useGiphy('grumpy cat');
  if (!round) {
    return <CircularProgress />;
  }
  return (
    <Screen title="GuesserNoGuess">
      <Card>
        <CardHeader
          title="Aussetzen"
          subheader={`Du hattest leider keine Idee. Das geheime Wort wäre '${round.term}' gewesen.`}
        />
        <CardContent>
          <Typography variant="h6">Du hattest folgende Hinweise:</Typography>
          <Hints switches={false}/>
        </CardContent>
        {gif &&
          <CardMedia
            component={Gif}
            gif={gif}
            width={600}
            backgroundColor="white"
            hideAttribution
          />
        }
        <CardActions>
          <Button
            variant="contained"
            startIcon={<SkipNext/>}
            onClick={nextRound}
          >
            weiter
          </Button>
        </CardActions>
      </Card>
    </Screen>
  );
};

const TipsterNoGuess = () => {
  const { round, guesser, nextRound } = useRound();
  const gif = useGiphy('angry cat');
  if (!round || !guesser) {
    return <CircularProgress />;
  }
  return (
    <Screen title="TipsterNoGuess">
      <Card>
        <CardHeader
          title="Aussetzen"
          subheader={`${guesser.name} hatte leider keine Idee. Das geheime Wort wäre '${round.term}' gewesen.`}
        />
        <CardContent>
          <Typography variant="h6">{guesser.name} hatte folgende Hinweise:</Typography>
          <Hints switches={false}/>
        </CardContent>
        {gif &&
          <CardMedia
            component={Gif}
            gif={gif}
            width={600}
            backgroundColor="white"
            hideAttribution
          />
        }
        <CardActions>
          <Button
            variant="contained"
            startIcon={<SkipNext/>}
            onClick={nextRound}
          >
            weiter
          </Button>
        </CardActions>
      </Card>
    </Screen>
  );
};

type HintsProps = {
  showInvalid?: boolean,
  switches?: boolean,
};

export const Hints: FC<HintsProps> = ({ showInvalid, switches }) => {
  const { hints } = useHints();
  if (!hints) {
    return <CircularProgress />;
  }
  const presentedHints = showInvalid ? hints : filter(hints, ({ invalid }) => !invalid);
  if (isEmpty(presentedHints)) {
    return <Typography>keine Hinweise vorhanden</Typography>;
  }
  return (
    <List>
      {map(presentedHints, ({ doc, player, term, invalid }) => {
        return (
          <ListItem key={doc.ref.path}>
            <ListItemAvatar>
              <Avatar src={player?.avatar}>{player?.name}</Avatar>
            </ListItemAvatar>
            <ListItemText primary={term} style={{ textDecoration: invalid ? 'line-through' : undefined }} />
            {switches &&
            <ListItemSecondaryAction>
              <Switch
                edge="end"
                checked={!invalid}
                onChange={(event) => doc.ref.update({ invalid: !event.target.checked } )}
              />
            </ListItemSecondaryAction>
            }
          </ListItem>
        );
      })
      }
    </List>
  );
};

export const Round = () => {
  const { round, isGuesser } = useRound();
  if (!round) {
    return <CircularProgress />;
  }
  switch (round.status) {
    case RoundStatus.COLLECTING_HINTS:
      if (isGuesser) {
        return <GuesserCollectingHints/>;
      }
      return <TipsterCollectingHints/>;
    case RoundStatus.GUESSING:
      if (isGuesser) {
        return <GuesserGuessing/>;
      }
      return <TipsterGuessing/>;
    case RoundStatus.CORRECT:     
      if (isGuesser) {
        return <GuesserCorrect/>;
      }
    return <TipsterCorrect/>;
    case RoundStatus.WRONG:
      if (isGuesser) {
        return <GuesserWrong/>;
      }
      return <TipsterWrong/>;
    case RoundStatus.NO_GUESS:
      if (isGuesser) {
        return <GuesserNoGuess/>;
      }
      return <TipsterNoGuess/>;
  }
  
  return (
    <Screen title="Round">
      <Card>
        <CardHeader
          title="Fehler"
          subheader="Dieser Fall ist nicht behandelt"
        />
        <CardContent>
          <div>Status: {round.status}</div>
        </CardContent>
      </Card>
    </Screen>
  );
};

export default Round;
